import { Component, OnInit, OnDestroy } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { Subject } from 'rxjs';
import { takeUntil, filter } from 'rxjs/operators';
import * as _ from 'lodash';
import { AuthHelper } from '@cation/core/auth/auth-helper';
import { parseJSON } from '@cation/core/util/common-util';
import { SharedService } from '@cation/core/services/shared/shared.service';
import { UserNotificationsDialogComponent } from '@cation/core/components/index';
import { PointsDialogComponent } from './points-dialog/points-dialog.component';
import { SidebarService } from '../shared/services/sidebar.service';
import { NAVIGATION_ITEMS, TEAM_ADMIN_NAV_ITEMS, GAMIFICATION_NAV_ITEMS, NavigationItem } from '../shared/configs/navigation.config';

/**
 * AppSidebarComponent - Enhanced sidebar with navigation integration
 * 
 * Features:
 * - Collapsible/expandable sidebar with state persistence
 * - Role-based navigation item filtering
 * - Active route detection and highlighting
 * - Team avatar, notifications, and points display
 * - Context-aware navigation (shows sub-items based on current route)
 * 
 * Requirements: 6.1, 6.2, 6.3, 6.4, 6.5, 6.6, 6.7, 6.8, 6.9
 */
@Component({
  selector: 'app-sidebar',
  templateUrl: './app-sidebar.component.html',
  styleUrls: ['./app-sidebar.component.scss']
})
export class AppSidebarComponent implements OnInit, OnDestroy {
  isOnline = false;
  countUnreadNotifications = 0;
  isCollapsed = false;
  navigationItems: NavigationItem[] = [];
  currentRoute = '';
  expandedItems: Set<string> = new Set(); // Track which items are expanded
  private notificationDialogRef: any = null;
  private pointsDialogRef: any = null;
  
  private destroy$ = new Subject<void>();

  constructor(
    public auth: AuthHelper,
    private sharedService: SharedService,
    private dialog: MatDialog,
    private sidebarService: SidebarService,
    private router: Router
  ) {}

  ngOnInit() {
    console.log('AppSidebarComponent:ngOnInit');
    
    // Subscribe to online status
    this.sharedService.currentOnlineStatus
      .pipe(takeUntil(this.destroy$))
      .subscribe((isOnline) => (this.isOnline = isOnline));

    // Subscribe to user profile changes
    console.log('AppSidebarComponent:currentUserProfile - Subscribe');
    this.auth.currentUserProfile$
      .pipe(takeUntil(this.destroy$))
      .subscribe((userProfile) => {
        console.log('AppSidebarComponent:currentUserProfile', userProfile);
        const isOnlineStatus = parseJSON(localStorage.getItem(`${userProfile.cognitoId}-isOnline`)) || {
          isOnline: false,
        };

        this.setOnlineStatus(isOnlineStatus.isOnline);
        
        // Filter navigation items based on user roles
        this.filterNavigationItems();
      });

    // Subscribe to notifications
    this.sharedService.currentUserNotifications
      .pipe(takeUntil(this.destroy$))
      .subscribe((data) => {
        this.countUnreadNotifications = _.filter(data.userNotifications, (i) => !i.isRead).length;
      });
    
    // Subscribe to sidebar collapsed state
    this.sidebarService.collapsed$
      .pipe(takeUntil(this.destroy$))
      .subscribe((collapsed) => {
        this.isCollapsed = collapsed;
      });
    
    // Subscribe to router events to track current route
    this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        takeUntil(this.destroy$)
      )
      .subscribe((event: NavigationEnd) => {
        this.currentRoute = event.urlAfterRedirects;
        // Re-filter navigation items when route changes
        this.filterNavigationItems();
      });
    
    // Set initial route
    this.currentRoute = this.router.url;
    
    // Filter navigation items on init
    this.filterNavigationItems();
  }
  
  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Filter navigation items based on user roles and current route
   * Shows context-aware navigation based on the current section
   * 
   * Requirement 6.1: Display navigation items with icons and collapsible text labels
   * Requirement 6.2: Filter navigation items based on user roles
   */
  private filterNavigationItems(): void {
    const userRoles = this.auth.getUserRole();
    
    if (!userRoles || userRoles.length === 0) {
      this.navigationItems = [];
      return;
    }
    
    // Determine which navigation to show based on current route
    let itemsToFilter: NavigationItem[] = [];
    
    if (this.currentRoute.startsWith('/admin/gamification')) {
      // Show gamification sub-navigation
      itemsToFilter = GAMIFICATION_NAV_ITEMS;
    } else if (this.currentRoute.startsWith('/admin')) {
      // Show team admin sub-navigation
      itemsToFilter = TEAM_ADMIN_NAV_ITEMS;
    } else {
      // Show default navigation (Profile)
      // itemsToFilter = NAVIGATION_ITEMS;
    }
    
    this.navigationItems = itemsToFilter.filter(item => {
      // Check if user has any of the required roles for this navigation item
      return item.roles.some(requiredRole => userRoles.includes(requiredRole));
    });
  }

  /**
   * Toggle sidebar collapsed/expanded state
   * 
   * Requirement 6.6: Support collapsible behavior to expand and collapse the sidebar width
   */
  toggleSidebar(): void {
    this.sidebarService.toggle();
  }

  /**
   * Check if a route is currently active
   * Supports exact matching and prefix matching for nested routes
   * 
   * @param route - The route to check
   * @returns true if the route is active, false otherwise
   * 
   * Requirement 6.3: Implement clear active state indicators
   */
  isActive(route: string): boolean {
    if (!route || !this.currentRoute) {
      return false;
    }
    
    // Exact match
    if (this.currentRoute === route) {
      return true;
    }
    
    // Prefix match for nested routes (e.g., /admin/dashboard matches /admin)
    // But avoid matching root route '/' with everything
    if (route !== '/' && this.currentRoute.startsWith(route + '/')) {
      return true;
    }
    
    return false;
  }

  /**
   * Navigate to route and auto-collapse sidebar on mobile/tablet
   * 
   * @param route - The route to navigate to
   */
  navigateAndCollapse(route: string): void {
    this.router.navigate([route]);
    
    // Auto-collapse sidebar after navigation
    this.sidebarService.setCollapsed(true);
  }

  /**
   * Handle clicks on legacy items (avatar, notifications, points)
   * Auto-collapse sidebar after interaction
   */
  handleItemClick(): void {
    // Auto-collapse sidebar after clicking any item
    this.sidebarService.setCollapsed(true);
  }

  /**
   * Check if a navigation item or any of its children are active
   * Used to keep parent items highlighted when child is active
   */
  isItemOrChildActive(item: NavigationItem): boolean {
    if (this.isActive(item.route)) {
      return true;
    }
    
    if (item.children) {
      return item.children.some(child => this.isActive(child.route));
    }
    
    return false;
  }

  /**
   * Toggle expansion state of navigation item with children
   */
  toggleExpanded(itemId: string, event?: MouseEvent): void {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    
    if (this.expandedItems.has(itemId)) {
      this.expandedItems.delete(itemId);
    } else {
      this.expandedItems.add(itemId);
    }
  }

  /**
   * Check if a navigation item is expanded
   */
  isExpanded(itemId: string): boolean {
    return this.expandedItems.has(itemId);
  }

  setOnlineStatus(status: boolean) {
    this.sharedService.setOnlineStatus(status);
  }

  toggleStatus($event: MouseEvent) {
    $event.preventDefault();
    $event.stopPropagation();

    this.isOnline = !this.isOnline;
    this.setOnlineStatus(this.isOnline);
    localStorage.setItem(`${this.auth.userProfile.cognitoId}-isOnline`, JSON.stringify({ isOnline: this.isOnline }));
  }

  openUserNotificationsDialog() {
    // Toggle behavior: close if already open, open if closed
    if (this.notificationDialogRef) {
      this.notificationDialogRef.close();
      this.notificationDialogRef = null;
    } else {
      this.notificationDialogRef = this.dialog.open(UserNotificationsDialogComponent, {
        autoFocus: false,
        width: '440px',
        maxWidth: '95vw',
        maxHeight: '80vh',
      });
      
      // Clear reference when dialog is closed
      this.notificationDialogRef.afterClosed().subscribe(() => {
        this.notificationDialogRef = null;
      });
    }
  }

  openPointsDialog() {
    if (this.pointsDialogRef) {
      this.pointsDialogRef.close();
      this.pointsDialogRef = null;
    } else {
      this.pointsDialogRef = this.dialog.open(PointsDialogComponent, {
        autoFocus: false,
        width: '380px',
        maxWidth: '95vw',
        panelClass: 'points-dialog-panel',
      });
      this.pointsDialogRef.afterClosed().subscribe(() => {
        this.pointsDialogRef = null;
      });
    }
  }

  closePointsDialog() {}
}
